// LandAreaBlock.jsx
// ✅ Price field shown FIRST
// ✅ range: true => price → RangeField (from/to), total_area → RangeLandTypeField (from/to + unit)
// ✅ range: false => price → InputBox (single), total_area → LandTypeField (single)

import React, { useState } from "react";
import { LandTypeField, RangeField, RangeLandTypeField } from "./formUtils";
import InputBox from "./InputBox";
import "./LandAreaBlock.css";

// ── Number → Words ────────────────────────────────────────────
const numberToWords = (num) => {
  const a = ["","One","Two","Three","Four","Five","Six","Seven","Eight","Nine",
    "Ten","Eleven","Twelve","Thirteen","Fourteen","Fifteen","Sixteen",
    "Seventeen","Eighteen","Nineteen"];
  const b = ["","","Twenty","Thirty","Forty","Fifty","Sixty","Seventy","Eighty","Ninety"];
  if (num === 0) return "Zero";
  const convert = (n) => {
    if (n < 20) return a[n];
    if (n < 100) return b[Math.floor(n/10)] + " " + a[n%10];
    if (n < 1000) return a[Math.floor(n/100)] + " Hundred " + convert(n%100);
    if (n < 100000) return convert(Math.floor(n/1000)) + " Thousand " + convert(n%1000);
    if (n < 10000000) return convert(Math.floor(n/100000)) + " Lakh " + convert(n%100000);
    return convert(Math.floor(n/10000000)) + " Crore " + convert(n%10000000);
  };
  return convert(num).trim();
};

const priceToWords = (n) => {
  if (!n || isNaN(n) || Number(n) <= 0) return "";
  return `${numberToWords(Number(n))} Only`;
};

// ── Main ─────────────────────────────────────────────────────
const LandAreaBlock = ({
  inputs,
  formData,
  onChange,
  errors,
  typeId,
  catId,
  isOther,
  visibleFn,
  requiredFn,
  showPrice = true,
}) => {
  const [selectedFields, setSelectedFields] = useState(null);
  const [popupOpen,      setPopupOpen]      = useState(false);
  const [tempSelected,   setTempSelected]   = useState([]);

  // ── All landtype fields (for area section) ────────────────
  const allLandFields = Object.values(inputs).filter(f =>
    f.is_show && f.landtype && visibleFn(f, typeId, catId, isOther)
  );

  // ── Price field (is_price_field: true) ────────────────────
  const priceInput      = Object.values(inputs).find(f => f.is_price_field && f.is_show);
  const isPriceVisible  = showPrice && priceInput && visibleFn(priceInput, typeId, catId, isOther);
  const isPriceRequired = priceInput && requiredFn(priceInput, typeId, catId, isOther);
  const isPriceRange    = priceInput?.range === true;

  // ── Price word (only for single price input, not range) ───
  const priceFieldKey = priceInput?.field || 'price';
  const singlePriceVal = formData[priceFieldKey];
  const priceWord = !isPriceRange ? priceToWords(singlePriceVal) : "";

  if (allLandFields.length === 0 && !isPriceVisible) return null;

  // ── Land field selection logic ────────────────────────────
  const requiredFields = allLandFields.filter(f => requiredFn(f, typeId, catId, isOther));
  const requiredKeys   = requiredFields.map(f => f.field);
  const defaultField   = allLandFields.find(f =>
    Array.isArray(f.is_land_primary)
      ? f.is_land_primary.includes(Number(catId))
      : f.is_land_primary === true
  ) || allLandFields[0];
  const isDefaultMode  = selectedFields === null;

  const getDisplayedFields = () => {
    if (isDefaultMode) {
      const keys = new Set(requiredKeys);
      if (defaultField && !keys.has(defaultField.field)) keys.add(defaultField.field);
      return allLandFields.filter(f => keys.has(f.field));
    }
    const keys = new Set([...requiredKeys, ...selectedFields]);
    return allLandFields.filter(f => keys.has(f.field));
  };

  const displayedFields = getDisplayedFields();

  const openPopup = () => {
    let current;
    if (isDefaultMode) {
      current = [...requiredKeys];
      if (defaultField && !requiredKeys.includes(defaultField.field))
        current.push(defaultField.field);
    } else {
      current = [...new Set([...requiredKeys, ...selectedFields])];
    }
    setTempSelected(current);
    setPopupOpen(true);
  };

  const toggleTemp = (field) => {
    if (requiredKeys.includes(field)) return;
    setTempSelected(prev =>
      prev.includes(field) ? prev.filter(f => f !== field) : [...prev, field]
    );
  };

  const handleDone = () => {
    const finalKeys = [...new Set([...requiredKeys, ...tempSelected])];
    setSelectedFields(finalKeys);
    setPopupOpen(false);
    allLandFields.forEach(f => {
      if (!finalKeys.includes(f.field)) {
        onChange(f.field, "");
        onChange(f.field + "_unit", undefined);
      }
    });
  };

  const removeField = (fieldKey) => {
    if (requiredKeys.includes(fieldKey)) return;
    const currentKeys = isDefaultMode
      ? [...new Set([...requiredKeys, defaultField?.field].filter(Boolean))]
      : [...new Set([...requiredKeys, ...selectedFields])];
    const next = currentKeys.filter(k => k !== fieldKey);
    setSelectedFields(next);
    onChange(fieldKey, "");
    onChange(fieldKey + "_unit", undefined);
  };

  // totalPrice — range field: price_from தொடக்கமாக use பண்ணுவோம்
  const totalPrice = isPriceRange
    ? (formData[priceInput?.range_from_field] ? Number(formData[priceInput.range_from_field]) : null)
    : (formData[priceFieldKey] ? Number(formData[priceFieldKey]) : null);

  return (
    <div className="lab-wrap">

      {/* ── 1. Price — range or single ── */}
      {isPriceVisible && (
        <div className="lab-price-wrap">
          {isPriceRange ? (
            // ✅ Range price: Min Price / Max Price இரண்டு inputs
            <RangeField
              fieldCfg={priceInput}
              formData={formData}
              onChange={onChange}
              errors={errors}
              req={isPriceRequired}
            />
          ) : (
            // Single price input
            <>
              <InputBox
                label={priceInput.label}
                field={priceInput.field}
                type={priceInput.type || "number"}
                placeholder={priceInput.placeholder || "Enter price"}
                value={formData[priceInput.field] || ""}
                onChange={onChange}
                isrequired={isPriceRequired}
                error={errors?.[priceInput.field]}
              />
              {priceWord && <div className="lt-price-rate">{priceWord}</div>}
            </>
          )}
        </div>
      )}

      {/* ── 2. Land type fields — range or single ── */}
      {allLandFields.length > 0 && (
        <>
          <div className="lab-fields">
            {displayedFields.map(f => {
              const isRequired = requiredKeys.includes(f.field);
              const canRemove  = !isRequired && !isDefaultMode;
              const isRange    = f.range === true;

              return (
                <div key={f.field} className="lab-field-row">
                  <div className="lab-field-inner">
                    {isRange ? (
                      // ✅ Range land area: Min Area — Max Area + unit selector
                      <RangeLandTypeField
                        fieldCfg={f}
                        formData={formData}
                        onChange={onChange}
                        errors={errors}
                        req={isRequired}
                      />
                    ) : (
                      // Single land area
                      <LandTypeField
                        fieldCfg={f}
                        formData={formData}
                        onChange={onChange}
                        errors={errors}
                        req={isRequired}
                        totalPrice={totalPrice}
                      />
                    )}
                  </div>
                  {canRemove && (
                    <button
                      type="button"
                      className="lab-remove-btn"
                      title={`Remove ${f.label}`}
                      onClick={() => removeField(f.field)}
                    >
                      ×
                    </button>
                  )}
                </div>
              );
            })}
          </div>

          <button type="button" className="add-more-btn lab-add-btn" onClick={openPopup}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" width="14" height="14">
              <line x1="12" y1="5" x2="12" y2="19"/>
              <line x1="5" y1="12" x2="19" y2="12"/>
            </svg>
            {isDefaultMode ? "Add More Area" : "Edit Area Fields"}
          </button>
        </>
      )}

      {/* ── Popup ── */}
      {popupOpen && (
        <div className="lab-popup-overlay" onClick={() => setPopupOpen(false)}>
          <div className="lab-popup" onClick={e => e.stopPropagation()}>
            <div className="lab-popup-header">
              <h3 className="lab-popup-title">Select Area Fields</h3>
              <button type="button" className="lab-popup-close" onClick={() => setPopupOpen(false)}>×</button>
            </div>
            <p className="lab-popup-hint">
              Choose the area measurements to include.
              <br/>
              <span className="lab-popup-hint-req">Fields marked * are required and cannot be removed.</span>
            </p>
            <div className="lab-popup-list">
              {allLandFields.map(f => {
                const isReq   = requiredKeys.includes(f.field);
                const checked = isReq || tempSelected.includes(f.field);
                return (
                  <label key={f.field}
                    className={`lab-popup-item${checked ? " lab-checked" : ""}${isReq ? " lab-locked" : ""}`}>
                    <input type="checkbox" checked={checked} disabled={isReq}
                      onChange={() => toggleTemp(f.field)} />
                    <span className="lab-popup-item-label">
                      {f.label}{isReq && <span className="lab-req-star"> *</span>}
                    </span>
                    {isReq && (
                      <span className="lab-popup-item-lock" title="Required">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" width="13" height="13">
                          <rect x="3" y="11" width="18" height="11" rx="2"/>
                          <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                        </svg>
                      </span>
                    )}
                    {!isReq && checked && (
                      <span className="lab-popup-item-tick">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" width="12" height="12">
                          <polyline points="20 6 9 17 4 12"/>
                        </svg>
                      </span>
                    )}
                  </label>
                );
              })}
            </div>
            <div className="lab-popup-footer">
              <button type="button" className="lab-popup-cancel-btn" onClick={() => setPopupOpen(false)}>Cancel</button>
              <button type="button" className="lab-popup-done-btn" onClick={handleDone}>Done</button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default LandAreaBlock;